import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, ShieldCheck, Phone, ArrowRight, CheckCircle2, Loader2,
  Layers, Lightbulb, Users, Clock, FileCheck2, CalendarClock,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { createLead } from "../lib/api";
import { CONTACT, IMAGES } from "../data/content";

const BUILDING_IMG =
  "https://images.pexels.com/photos/18153132/pexels-photo-18153132.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const BENEFITS = [
  { icon: Layers, title: "Mehrere Liegenschaften gebündelt", text: "Strom und Gas für alle Objekte zusammengeführt – mehrere Lieferstellen in einem Rahmenvertrag, weniger Aufwand." },
  { icon: Lightbulb, title: "Allgemeinstrom & Gas", text: "Treppenhaus, Aufzug, Tiefgarage, Heizung & Gemeinschaftsanlagen: Wir prüfen jeden Zählpunkt und vergleichen passende Tarife." },
  { icon: FileCheck2, title: "Bestehende Verträge prüfen", text: "Wir sehen uns Ihre laufenden Energieverträge an und vergleichen sie unabhängig mit dem aktuellen Markt." },
  { icon: CalendarClock, title: "Laufzeiten & Fristen im Blick", text: "Wir behalten Vertragslaufzeiten und Kündigungsfristen Ihrer Lieferstellen im Auge, damit kein Wechseltermin verpasst wird." },
  { icon: Users, title: "Persönliche, unabhängige Beratung", text: "Kein Callcenter. Sie sprechen direkt mit Kerstin Hemming – anbieterübergreifend und auf Ihre Anforderungen abgestimmt." },
  { icon: ShieldCheck, title: "Weniger Verwaltungsaufwand", text: "Ein Ansprechpartner für alle Objekte, auf Wunsch inkl. Wechselservice – kostenlos und unverbindlich." },
];

const STEPS = [
  { n: "01", title: "Anfrage senden", text: "Name, E-Mail und Telefon genügen. Kein Papierkram nötig." },
  { n: "02", title: "Objekte prüfen", text: "Wir sichten Ihre Zählpunkte und vergleichen den Markt für Allgemein- und Objektstrom." },
  { n: "03", title: "Angebot in 24 h", text: "Sie erhalten eine klare, gebündelte Empfehlung – auf Wunsch inkl. Wechselservice." },
];

const fireLeadConversion = () => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "generate_lead",
    lead_type: "hausverwaltung",
    form_location: "hausverwaltungen_landingpage",
  });
  if (typeof window.gtag === "function") {
    window.gtag("event", "generate_lead", {
      lead_type: "hausverwaltung",
      form_location: "hausverwaltungen_landingpage",
    });
  }
};

export default function Hausverwaltungen() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError("Bitte füllen Sie Name, E-Mail und Telefon aus.");
      return;
    }
    setLoading(true);
    try {
      await createLead({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        type: "hausverwaltung",
        source: "landingpage-hausverwaltungen",
        topic: "Hausverwaltung",
        message: "Anfrage über Landingpage /hausverwaltungen (Strom-/Gasbündelung & Allgemeinstrom).",
      });
      fireLeadConversion();
      setSubmitted(true);
    } catch (err) {
      setError("Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut oder rufen Sie uns an.");
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById("anfrage")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-white text-navy-900" data-testid="hausverwaltungen-page">
      <Seo
        title="Energie für Hausverwaltungen – gebündelt & günstiger"
        description="Strom- & Gasverträge für Ihre Liegenschaften gebündelt beschaffen und Allgemeinstrom senken. Unabhängig, kostenlos, Angebot in 24 Stunden. Jetzt Anfrage senden."
        path="/hausverwaltungen"
        noindex
      />

      {/* Minimal top bar */}
      <header className="border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="hv-logo">
            <span className="h-9 w-9 rounded-xl bg-blue-600 grid place-items-center text-white">
              <Building2 className="h-5 w-5" />
            </span>
            <span className="font-heading font-bold text-lg tracking-tight">Energie·Tarifberater</span>
          </Link>
          <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-800" data-testid="hv-phone">
            <Phone className="h-4 w-4" /> {CONTACT.phone}
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hv-hero">
        <div className="absolute inset-0">
          <img src={BUILDING_IMG} alt="Moderne Wohngebäude" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/92 to-white/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-white/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-blue-700 mb-6">
              <Building2 className="h-3.5 w-3.5" /> Für Hausverwaltungen
            </span>
            <h1 className="font-heading font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              Energiekosten Ihrer Liegenschaften{" "}
              <span className="text-blue-600">spürbar senken</span>.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Wir bündeln die Strom- &amp; Gasverträge all Ihrer Objekte und optimieren den
              Allgemeinstrom – unabhängig, kostenlos und mit nur einem Ansprechpartner.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={scrollToForm}
                data-testid="hv-cta-primary"
                className="group inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 shadow-lg shadow-blue-600/20 transition-all duration-200 hover:-translate-y-0.5 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
              >
                Kostenlose Prüfung anfragen
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <a
                href={CONTACT.phoneHref}
                data-testid="hv-cta-call"
                className="inline-flex items-center gap-2 rounded-lg bg-white text-navy-900 font-semibold py-4 px-8 border border-slate-200 shadow-sm transition-all duration-200 hover:-translate-y-0.5"
              >
                <Phone className="h-4 w-4 text-blue-600" /> Direkt anrufen
              </a>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
              {["100 % kostenlos & unverbindlich", "Unabhängig – kein Callcenter", "Angebot in 24 Stunden"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-blue-600" /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl max-w-2xl">
              Warum Hausverwaltungen mit uns arbeiten
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl text-lg">
              Ob Eigentümergemeinschaft, Mietobjekt oder gemischtes Portfolio – wir schaffen Übersicht
              und finden Einsparpotenziale über alle Zählpunkte hinweg.
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.06}>
                <div className="h-full bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-7" data-testid={`hv-benefit-${i}`}>
                  <span className="inline-grid place-items-center h-11 w-11 rounded-lg bg-blue-50 text-blue-600 mb-5">
                    <b.icon className="h-5 w-5" />
                  </span>
                  <h3 className="font-heading font-semibold text-lg mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl">So einfach geht’s</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="relative bg-white rounded-xl border border-slate-200 shadow-sm p-8" data-testid={`hv-step-${i}`}>
                  <span className="absolute right-6 top-4 text-6xl font-heading font-extrabold text-slate-100 select-none">{s.n}</span>
                  <h3 className="font-heading font-semibold text-xl mb-3 relative">{s.title}</h3>
                  <p className="text-slate-600 leading-relaxed relative">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="anfrage" className="relative py-20 sm:py-24 bg-navy-900 text-white overflow-hidden scroll-mt-16">
        <div className="absolute inset-0 opacity-20">
          <img src={IMAGES.heroAbstract} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy-900/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl leading-tight">
              Kostenlose Prüfung für Ihre Objekte
            </h2>
            <p className="mt-5 text-blue-100/90 text-lg leading-relaxed max-w-lg">
              Senden Sie uns Ihre Kontaktdaten – wir melden uns innerhalb von 24 Stunden mit den
              nächsten Schritten. Keine Verpflichtung, keine Kosten.
            </p>
            <ul className="mt-8 space-y-3">
              {["Gebündelte Beschaffung über alle Zählpunkte", "Allgemeinstrom-Optimierung inklusive", "Auf Wunsch kompletter Wechselservice"].map((t) => (
                <li key={t} className="flex gap-3 text-blue-50">
                  <CheckCircle2 className="h-5 w-5 text-blue-300 shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-7 sm:p-9 text-navy-900 shadow-2xl">
            {!submitted ? (
              <form onSubmit={submit} className="space-y-5" data-testid="hv-form">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Name *</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none px-4 py-3 text-sm transition-colors"
                    placeholder="Ihr Name / Hausverwaltung"
                    data-testid="hv-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">E-Mail *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none px-4 py-3 text-sm transition-colors"
                    placeholder="name@hausverwaltung.de"
                    data-testid="hv-email-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Telefon *</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none px-4 py-3 text-sm transition-colors"
                    placeholder="Für den Rückruf"
                    data-testid="hv-phone-input"
                  />
                </div>

                {error && <p className="text-sm text-rose-600" data-testid="hv-form-error">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-4 shadow-lg shadow-blue-600/20 transition-all duration-200 hover:-translate-y-0.5 flex items-center justify-center gap-2"
                  data-testid="hv-submit-btn"
                >
                  {loading ? (<><Loader2 className="h-5 w-5 animate-spin" /> Wird gesendet…</>) : (<>Kostenlose Prüfung anfragen <ArrowRight className="h-4 w-4" /></>)}
                </button>
                <p className="flex items-center gap-2 text-xs text-slate-400 justify-center">
                  <Clock className="h-3.5 w-3.5" /> Antwort in der Regel innerhalb von 24 Stunden
                </p>
              </form>
            ) : (
              <div className="text-center py-6" data-testid="hv-success">
                <span className="inline-grid place-items-center h-16 w-16 rounded-full bg-emerald-50 text-emerald-600 mb-5">
                  <CheckCircle2 className="h-9 w-9" />
                </span>
                <h3 className="font-heading font-bold text-2xl mb-2">Vielen Dank, {form.name.split(" ")[0]}!</h3>
                <p className="text-slate-600 leading-relaxed">
                  Ihre Anfrage ist bei uns eingegangen. Kerstin Hemming meldet sich innerhalb von
                  24 Stunden persönlich bei Ihnen unter <span className="font-semibold text-navy-900">{form.phone}</span>.
                </p>
                <a href={CONTACT.phoneHref} className="mt-6 inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 transition-colors">
                  <Phone className="h-4 w-4" /> Lieber sofort anrufen
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Slim footer */}
      <footer className="bg-navy-950 text-slate-400 py-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <p>© {new Date().getFullYear()} {CONTACT.company} · {CONTACT.responsible}</p>
          <div className="flex items-center gap-6">
            <a href={CONTACT.phoneHref} className="hover:text-white transition-colors">{CONTACT.phone}</a>
            <Link to="/impressum" className="hover:text-white transition-colors" data-testid="hv-impressum">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-white transition-colors" data-testid="hv-datenschutz">Datenschutz</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
